"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function AiRevolutionHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="191970" id="מרכז-המידע-של-siudy" className="bg-background text-foreground relative min-h-[100svh] flex items-center justify-center overflow-hidden py-24" dir="rtl">
      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[50vw] h-[50vw] rounded-lg bg-primary/5 mix-blend-multiply blur-[120px] animate-pulse opacity-60" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[60vw] h-[60vw] rounded-lg bg-secondary/10 mix-blend-multiply blur-[150px] animate-pulse opacity-50" style={{
        animationDelay: "2s",
        animationDuration: "7s"
      }} />
        <div className="absolute top-[40%] left-[20%] w-[30vw] h-[30vw] rounded-lg bg-ring/5 mix-blend-multiply blur-[100px] animate-pulse opacity-40" style={{
        animationDelay: "4s",
        animationDuration: "5s"
      }} />
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#0F172A08_1px,transparent_1px),linear-gradient(to_bottom,#0F172A08_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      <div ref={ref} className={`relative z-10 container mx-auto px-4 flex flex-col items-center text-center max-w-5xl transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        {}
        <div className="relative mb-8">
          {}
          <h1 className="absolute inset-0 text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black font-default tracking-tighter blur-2xl opacity-20 bg-gradient-to-r from-primary via-secondary to-ring bg-clip-text text-transparent select-none" aria-hidden="true">
            מרכז המידע של Siudy
          </h1>
          {}
          <h1 className="relative text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black font-default tracking-tighter bg-gradient-to-br from-primary via-primary/80 to-secondary bg-clip-text text-transparent drop-shadow-sm">
            מרכז המידע של Siudy
          </h1>
        </div>

        {}
        <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-3xl leading-relaxed mb-12 font-default font-medium">
          מדריכים מקצועיים, זכויות מול ביטוח לאומי וכל המידע שאתם צריכים כדי
          להבטיח טיפול סיעודי איכותי ליקיריכם. אנחנו כאן כדי לעזור למשפחה שלכם לנווט בביטחון.
        </p>

        {}
        <div className="relative group mb-12">
          <div className="absolute -inset-1 bg-gradient-to-r from-primary to-ring rounded-lg blur-lg opacity-20 group-hover:opacity-40 transition duration-500 group-hover:duration-200" />
          <Button asChild size="lg" className="relative h-16 px-10 text-xl font-default rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-lg shadow-primary/20">
            <Link to="#">
              לכל המאמרים המקצועיים
              <ArrowLeft className="mr-3 size-6 transition-transform group-hover:-translate-x-1" />
            </Link>
          </Button>
        </div>

        {}
        <Badge variant="outline" className="px-4 py-2 text-sm sm:text-base font-default border-primary/10 bg-white/80 backdrop-blur-md text-primary shadow-sm">
          <Sparkles className="size-4 ml-2 text-secondary animate-pulse" />
          מעל 30 מאמרי מומחים בתחום הסיעוד
        </Badge>
      </div>
    </section>;
}